import { useEffect, useState, type ReactNode } from "react";
import { TOPBAR_H } from "./layout";

/**
 * The studio top bar — the approved mockup's (2026-09-29): wordmark · file
 * name · … · Undo Redo · Compare · Basic|Advanced · host actions · Export.
 *
 * The bar mounts BEFORE the editor providers do (the chrome waits for the
 * viewer handshake), so the parts that need the editor store — the file name,
 * Undo/Redo/Compare/mode switch and the Export button — are portalled into the
 * slots below from inside EditorChrome, where that context exists. The host
 * (Vault) passes its own actions straight in.
 */
export const TOPBAR_SLOT = {
  file: "studio-topbar-file",
  tools: "studio-topbar-tools",
  primary: "studio-topbar-primary",
} as const;

export function TopBar({ leading, actions }: { leading?: ReactNode; actions?: ReactNode }) {
  return (
    <header
      data-studio-topbar
      className="relative z-30 flex shrink-0 items-center gap-2.5 overflow-x-auto border-b border-ed-border bg-ed-panel pl-4 pr-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{ height: TOPBAR_H }}
    >
      <div className="flex shrink-0 select-none items-baseline gap-1.5 min-[1200px]:min-w-[220px]" data-studio-wordmark>
        <span className="text-[16px] font-semibold tracking-[-0.01em] text-ed-text">Jewelitt</span>
        <span className="text-[16px] font-medium text-ed-gold max-[520px]:hidden">Studio</span>
      </div>
      {leading}
      <div id={TOPBAR_SLOT.file} className="flex min-w-0 items-center max-[700px]:hidden" />
      <span className="min-w-4 flex-1" />
      <div id={TOPBAR_SLOT.tools} className="flex shrink-0 items-center gap-2.5" />
      {actions ? <div className="flex shrink-0 items-center gap-2.5">{actions}</div> : null}
      <div id={TOPBAR_SLOT.primary} className="flex shrink-0 items-center" />
    </header>
  );
}

/** The slot element once it exists (the bar renders first; this runs after). */
export function useTopBarSlot(id: string): HTMLElement | null {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setEl(document.getElementById(id));
  }, [id]);
  return el;
}

/** Shared top-bar button look, so host actions match the editor's own. */
export const topBarButton =
  "inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium text-ed-text transition-colors hover:bg-ed-hover disabled:cursor-not-allowed disabled:opacity-40";
